import Request from './request';
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import { getEntityTypeByClass } from '../utils/FeatureHelper';
import { getSaveableDateWithTime } from "../utils/DateHelper";
import FieldNames from "../defaults/FieldNames.json";
import GeneralService from './GeneralService';
import { getAppConfig, getAttributeServiceUrl, getNifcToken } from '../utils/ReduxHelper';

const AuditLogService = {
  getEntityAuditLog: function(joinField, joinID) {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const queryObject = new Query({
        where: `(${FieldNames.auditLogFields.joinID} = ${joinID} AND ${FieldNames.auditLogFields.joinFeature} = '${joinField}')`,
        orderByFields: ["ModifiedDate DESC","OBJECTID DESC"],
        outFields: "*",
        returnGeometry: false,
      });

      return query.executeQueryJSON(rootServiceUrl + "/" + config.auditLog[config.env].layerId, queryObject).then(results => {
        let userRoles = [];
        if(results.features) {
          userRoles = results.features.map((eaItem) => eaItem.attributes);
        } 
        return userRoles;
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  addAuditLogEntry: function (entity, joinID, joinFeature, modifiedBy, message) {
    try
    {
      if(!message || (Array.isArray(message) && message.filter(x => x).length === 0))
      {
        // resolve with empty promise if no message is provided
        return Promise.resolve();
      }
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      const typeOverride = joinFeature === FieldNames.auditLogJoinTypes.users? "User" :
                            joinFeature === FieldNames.auditLogJoinTypes.budget ? "Budget" : 
                            joinFeature === FieldNames.auditLogJoinTypes.project ? "Project" : null;
      const auditLogEntry = {
        JoinID: joinID ?? entity[FieldNames.featureFields.joinID],
        JoinFeature: joinFeature,
        EntityType: typeOverride ?? entity[FieldNames.featureFields.entityType] ?? getEntityTypeByClass(entity[FieldNames.featureFields.class]) ?? "ERROR",
        EntityID: entity[FieldNames.featureFields.entityID] ?? entity[FieldNames.featureFields.objectID],
        ModifiedBy: modifiedBy,
        ModifiedDate: getSaveableDateWithTime(),
        AuditEntryJson: JSON.stringify([message])
      }
      const newAuditLogEntry = 
      {
          attributes: auditLogEntry
      }
      return new Promise((resolve, reject) => {
        new Request().makeRequest({
          url: rootServiceUrl + "/" + config.auditLog[config.env].layerId + "/addFeatures",
          method: 'post',
          data: {
              f: 'json',
              token: nifcToken,
              features: JSON.stringify([newAuditLogEntry])
          }
        }).then(
            resp => {
              resolve(resp)
            },
            error => {
              reject(error)
            }
        );
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }

  },
  addMultipleAuditLogs: async function (
    joinFeature, entityList, modifiedBy, auditEntry
  ) {
    try
    {
      if(!entityList || entityList.length === 0)
      {
        const emptyResults = {
        successful: [],
        failures: [],
      };
        return Promise.resolve(emptyResults);
      }
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      const typeOverride = joinFeature === FieldNames.auditLogJoinTypes.users ? "User" :
      joinFeature === FieldNames.auditLogJoinTypes.budget ? "Budget" : null;
      let allFinalResults = [];
      let auditLogList = [];
      entityList.forEach((entity) => 
      {
        const auditLogEntry = {
          JoinID: entity[FieldNames.featureFields.joinID] ?? entity[FieldNames.featureFields.id],
          JoinFeature: joinFeature,
          EntityType: typeOverride ?? entity[FieldNames.featureFields.entityType] ?? getEntityTypeByClass(entity[FieldNames.featureFields.class]) ?? "ERROR",
          EntityID: entity[FieldNames.featureFields.entityID] ?? entity[FieldNames.featureFields.objectID],
          ModifiedBy: modifiedBy,
          ModifiedDate: getSaveableDateWithTime(),
          AuditEntryJson: JSON.stringify([auditEntry])
        }
        const newAuditLogEntry = 
        {
            attributes: auditLogEntry
        }

        auditLogList.push(newAuditLogEntry);
      });

      let featureUrl = `${rootServiceUrl}/${
        config.auditLog[config.env].layerId
      }/addFeatures`;

      let newLogs = [];
      auditLogList.forEach((log) => {
        const newFeature = {
          attributes: log.attributes,
        };
        newLogs.push(newFeature);
      });
      const numberToTakeEachTime = 500;
      let startCount = 0;
      let hasMoreRecords = true;
      let finalResults = "";
      while (hasMoreRecords) {
        const numberToTake =
          startCount + numberToTakeEachTime > newLogs.length
            ? newLogs.length - startCount
            : numberToTakeEachTime;
        let numbersToRun = newLogs.slice(
          startCount,
          numberToTake + startCount
        );
        try {
          finalResults = await GeneralService.updateCallForMultiples(
            featureUrl,
            nifcToken,
            numbersToRun
          );
          if (finalResults && (finalResults.addResults || finalResults.updateResults)) {
            allFinalResults.push(finalResults.addResults ?? finalResults.updateResults);
          }
        } catch (e) {
          numbersToRun.forEach((item) => {
            const newItem = {
              objectId: item.attributes[FieldNames.featureFields.objectID],
              success: false,
            };
            allFinalResults.push(newItem);
          });
        }
        hasMoreRecords =
        newLogs.length > startCount + numberToTakeEachTime;
        startCount += numberToTakeEachTime;
      }
      let failedItems = [];
      let successfulItems = [];
      if (!hasMoreRecords) {
        allFinalResults.flat().forEach((result) => {
          if (result) {
            if (!result.success) {
              failedItems.push(result.objectId);
            } else {
              successfulItems.push(result.objectId);
            }
          }
        });
      }

      const allItemResults = {
        successful: successfulItems,
        failures: failedItems,
      };
      return allItemResults;
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
};

export default AuditLogService;